import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { learningPathQueryOptions, pathStepQueryOptions } from '@/features/courses/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { parseId } from '@/screens/courses/lib/params'

/**
 * The `/courses/path/$stepId` loader.
 *
 * MAIN: the step — its video, learn points, terms, and the next one. `null`
 * (`LEARNING_STEP_NOT_FOUND`) is the screen's not-found state. A `$stepId`
 * that is not a positive integer is answered without a request.
 *
 * SECONDARY, only for a real navigation: the whole path. `order` is only
 * mm-be's per-path sort key (`LearningPathStep.order`, unique but never
 * guaranteed to start at 1 or run without gaps) — "Step N of M" is this
 * step's rank among the path's OTHER steps, which only the full list can
 * answer, so the screen reads it through `useLearningPath` and simply omits
 * that line if the path fetch is still in flight, 404s, or fails; the step
 * itself never waits on it.
 */
export async function loadPathStep({
  context: { queryClient },
  params,
  preload,
}: {
  context: RouterContext
  params: { stepId: string }
  preload: boolean
}): Promise<void> {
  const stepId = parseId(params.stepId)
  if (stepId === null) return
  if (!preload) prefetchRouteData(queryClient, learningPathQueryOptions())
  await ensureRouteData(queryClient, pathStepQueryOptions(stepId))
}
